import React from 'react'
import { Box, Typography } from '@mui/material'
import Loader from 'src/designSystem/Loader'
import { useStalledAfterDays } from './api/AdvancedSettingsProvider'
import { useAdvancedOverviewState } from './api/AdvancedOverviewProvider'
import { deriveAttention } from './api/selectors'
import { useTrackCatalog } from './api/useTrackCatalog'
import { useParticipantIdentity } from './useParticipantName'
import { advancedColors, onDark } from './theme'
import { formatDay, formatDayTime, humanizeState } from './format'
import CareTeamSection from './dashboard/careTeam/CareTeamSection'
import EmptyNote from './components/EmptyNote'
import Section, { drawerCardSx } from './components/Section'
import Fact from './components/Fact'
import IdentityHeader from './components/IdentityHeader'
import TagsSection from './components/TagsSection'
import TherapySection from './components/TherapySection'
import QuickNoteSection from './components/QuickNoteSection'
import ScoreGraphSection from './components/ScoreGraphSection'
import SymptomSurveysSection from './components/SymptomSurveysSection'
import TodaysRecommendation from './components/TodaysRecommendation'
import TrackSummaryList from './components/TrackSummaryList'
import DeviceSection from './components/DeviceSection'
import { tracksUnavailableHeadline } from './tracksUnavailableHeadline'
import { TOUR_ANCHORS } from './tour/anchors'

const NO_PROGRAM_KEY = 'no-program'

type Props = { uid: string }

const AdvancedInfoArea = ({ uid }: Props) => {
  const catalog = useTrackCatalog()
  const state = useAdvancedOverviewState()
  const identity = useParticipantIdentity(uid)
  const stalledAfterDays = useStalledAfterDays()

  const summary = state.status === 'ready' ? state.overview.summary : null
  const attention = summary ? deriveAttention(summary, stalledAfterDays) : null

  return (
    <Box
      data-tour={TOUR_ANCHORS.infoArea}
      sx={{
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: advancedColors.deep,
        color: onDark.primary,
      }}
    >
      <IdentityHeader uid={uid} identity={identity} />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 2,
          py: 2,
          // A flex column would shrink these: each card sets overflow:hidden, so its flex minimum is 0.
          '& > * + *': { mt: 1.5 },
        }}
      >
        <Box sx={drawerCardSx}>
          <TagsSection uid={uid} grouped />
          <Box data-tour={TOUR_ANCHORS.symptomSurveys}>
            <SymptomSurveysSection uid={uid} timezone={identity.timezone} grouped canManageSurveys />
          </Box>
        </Box>
        <TherapySection uid={uid} />
        <QuickNoteSection key={uid} uid={uid} />
        <TodaysRecommendation uid={uid} programID={identity.programId} />

        {state.status === 'loading' && (
          <Box sx={{ py: 4 }}>
            <Loader size="small" />
          </Box>
        )}

        {summary && (
          <Section title="Tracks">
            {summary.tracks.length === 0 ? (
              <EmptyNote>No tracks yet.</EmptyNote>
            ) : (
              <TrackSummaryList
                tracks={summary.tracks}
                catalog={catalog}
                stalledKeys={attention?.stalledTracks ?? []}
              />
            )}
            {summary.startedAt && (
              <Box sx={{ mt: 1 }}>
                <Fact label="First enrolled" value={formatDay(summary.startedAt)} />
              </Box>
            )}
          </Section>
        )}

        {state.status !== 'loading' && state.status !== 'ready' && (
          <Section title="Tracks">
            <Typography variant="subtitle1" sx={{ color: onDark.secondary }}>
              {tracksUnavailableHeadline(state.status === 'unavailable')}.
            </Typography>
          </Section>
        )}

        <ScoreGraphSection uid={uid} type="PHQ9" />
        <ScoreGraphSection uid={uid} type="GAD7" />

        {state.status === 'ready' && (
          <Box data-tour={TOUR_ANCHORS.careTeam}>
            <CareTeamSection uid={uid} programId={identity.programId ?? NO_PROGRAM_KEY} />
          </Box>
        )}

        <Section title="Details">
          <Fact label="Status" value={identity.enrollmentState ? humanizeState(identity.enrollmentState) : '—'} />
          <Fact label="Program ID" value={identity.programId || '—'} />
          <Fact label="Access code" value={identity.accessCode || '—'} />
          <Fact label="Created at" value={formatDayTime(identity.createdAt)} />
          <Fact label="First login" value={formatDayTime(identity.firstTimeLoginAt)} />
          <Fact label="Time zone" value={identity.timezone || '—'} />
          <Fact label="Language" value={identity.language || 'English'} />
        </Section>

        <DeviceSection uid={uid} hrvDevice={identity.hrvDevice} />
      </Box>
    </Box>
  )
}

export default AdvancedInfoArea
